import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { find, isEmpty, last } from 'lodash';
import { useEventListener } from 'taboola-ultimate-ui';
import { DRAWER_MODE } from 'components/SideDrawer/SideDrawer';
import { FORM_MODES } from '../../../config/formModes';
import { BASE_FORM_ROUTE_PATH, FORM_MODE_TO_PATH_MAP } from '../../../config/routes';
import { useCurrentValueGetter } from '../../../hooks';
import { useDrawerStack } from '../../../hooks/queryParams/useDrawerStack';
import { useModuleConfigAttribute } from '../../../hooks/useModuleConfigAttribute';
import { useChatAgentContext } from '../../taboola-common-frontend-modules/chat-agent/hooks/useChatAgentContext';
import { hasFormMode } from '../../taboola-common-frontend-modules/forms/utils/hasFormMode';
import { matchPathWithoutQuery } from '../../taboola-common-frontend-modules/query-params';
import { useMultiLayerDrawerContext } from '../components/MultiLayerDrawerProvider/MultiLayerDrawerProvider';
import { getParams } from '../utils/getParams';
import { calcLayerShift, getLayerWidthPx, getRestrictedLayerWidthPx } from '../utils/layerStyleUtils';
import { useMultiLayerDrawerEnabled } from './useMultiLayerDrawerEnabled';

export const useMultiLayerDrawer = () => {
    const { stack, pop, push } = useDrawerStack();
    const { forms, moduleProviders } = useMultiLayerDrawerContext();
    const isMultiLayerDrawerEnabled = useMultiLayerDrawerEnabled();
    const location = useLocation();
    const getLocation = useCurrentValueGetter(location);

    const drawerStack = useMemo(() => [...stack, {}], [stack]);
    const disableMultiLayerDrawer = useModuleConfigAttribute({
        attribute: 'disableMultiLayerDrawer',
    });

    const { chatAgentWidth } = useChatAgentContext();

    //TODO:DEV-199810 Fix exporting styles.sideDrawerLayer doesn't work in unit test files
    const [screenWidth, setScreenWidth] = useState(window.innerWidth - chatAgentWidth);

    const updateScreenWidth = useCallback(() => {
        if (drawerStack.some(({ path }) => !!path)) {
            setScreenWidth(window.innerWidth - chatAgentWidth);
        }
    }, [chatAgentWidth, drawerStack]);

    useEventListener('resize', updateScreenWidth);

    useEffect(() => {
        updateScreenWidth();
    }, [updateScreenWidth]);

    const getDrawerData = useCallback(
        index => {
            const { path } = drawerStack[index] || {};
            const form = find(forms, ({ path: pattern }) => !!matchPathWithoutQuery(path, { path: pattern }));
            const { component: Component, props, path: pattern, drawerMode } = form || {};
            const { module } = getParams(path, pattern);
            const ModuleProvider = moduleProviders[module]?.provider;

            return {
                path,
                Component,
                props,
                pattern,
                drawerMode,
                ModuleProvider,
            };
        },
        [drawerStack, forms, moduleProviders]
    );

    const layerStyleList = useMemo(() => {
        const formWidths = drawerStack
            .filter(({ path }) => !!path)
            .map(({ path }, index) => {
                const form = find(forms, ({ path: pattern }) => !!matchPathWithoutQuery(path, { path: pattern }));
                const { mode } = getParams(path, BASE_FORM_ROUTE_PATH);
                const isCreateFormMode = mode === FORM_MODE_TO_PATH_MAP[FORM_MODES.CREATE];
                const drawerMode = isCreateFormMode ? DRAWER_MODE.LARGE : form?.drawerMode || DRAWER_MODE.SMALL;
                const basicWidth = getLayerWidthPx(drawerMode, screenWidth);
                const width = getRestrictedLayerWidthPx(basicWidth, index, screenWidth);

                return { drawerMode, basicWidth, width };
            });
        let topLayerWidth = last(formWidths)?.width;
        let aboveLayerWidth = topLayerWidth;
        const drawerSize = formWidths.length;

        for (let index = drawerSize - 2; index >= 0; index--) {
            formWidths[index].right =
                calcLayerShift({
                    aboveLayerWidth,
                    topLayerWidth,
                    drawerSize,
                    width: formWidths[index].width,
                    index,
                    screenWidth,
                }) + chatAgentWidth;
            aboveLayerWidth = formWidths[index].right + formWidths[index].width;
        }

        return formWidths;
    }, [drawerStack, forms, screenWidth, chatAgentWidth]);

    const getLayerStyle = useCallback(
        index => {
            return {
                right: layerStyleList[index]?.right ?? chatAgentWidth,
                width: layerStyleList[index]?.width,
            };
        },
        [layerStyleList, chatAgentWidth]
    );

    useLayoutEffect(() => {
        if (
            !isMultiLayerDrawerEnabled ||
            !isEmpty(stack) ||
            !hasFormMode(getLocation().pathname) ||
            disableMultiLayerDrawer
        ) {
            return;
        }
        push({ path: getLocation().pathname });
    }, [stack, getLocation, isMultiLayerDrawerEnabled, push, disableMultiLayerDrawer]);

    return {
        isMultiLayerDrawerEnabled,
        drawerStack,
        pop,
        getDrawerData,
        getLayerStyle,
    };
};
